import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Camera, Check, X, Loader2, ImageIcon, Clock, ThumbsUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function DishPhotosManagePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchPhotos = async () => {
    try {
      const res = await fetch(`${API}/business/dish-photos`, { headers });
      if (res.ok) setData(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchPhotos(); }, []);

  const updateStatus = async (photoId, action) => {
    setProcessing(photoId);
    try {
      const res = await fetch(`${API}/business/dish-photos/${photoId}/${action}`, {
        method: 'PUT', headers
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      const d = await res.json();
      toast.success(d.message);
      fetchPhotos();
    } catch (err) { toast.error(err.message); }
    setProcessing(null);
  };

  if (loading) return <div className="flex justify-center items-center min-h-screen"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  const photos = data || { pending: [], approved: [], rejected: [], total: 0 };
  const currentPhotos = photos[activeTab] || [];

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-white border-b px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Link to="/business/dashboard"><Button variant="ghost" size="icon"><ArrowLeft className="w-5 h-5" /></Button></Link>
          <div>
            <h1 className="text-xl font-bold" data-testid="dish-photos-title">Photos clients</h1>
            <p className="text-sm text-muted-foreground">
              {photos.pending.length} en attente · {photos.approved.length} approuvées · {photos.total} total
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Tabs */}
        <div className="flex gap-2" data-testid="photo-manage-tabs">
          {[
            { id: 'pending', label: 'En attente', icon: Clock, count: photos.pending.length, color: 'bg-amber-500' },
            { id: 'approved', label: 'Approuvées', icon: ThumbsUp, count: photos.approved.length, color: 'bg-emerald-500' },
            { id: 'rejected', label: 'Refusées', icon: X, count: photos.rejected.length, color: 'bg-red-500' },
          ].map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab(tab.id)}
              className="rounded-full gap-1.5"
              data-testid={`photo-tab-${tab.id}`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
              {tab.count > 0 && (
                <Badge className={`${activeTab === tab.id ? 'bg-white/20 text-white' : tab.color + ' text-white'} text-[10px] px-1.5 ml-0.5`}>
                  {tab.count}
                </Badge>
              )}
            </Button>
          ))}
        </div>

        {/* Info banner for pending */}
        {activeTab === 'pending' && photos.pending.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
            <strong>Astuce :</strong> En approuvant une photo, le client gagne +20 points et la photo apparaît dans votre galerie publique. C'est gagnant-gagnant !
          </div>
        )}

        {/* Photos grid */}
        {currentPhotos.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ImageIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-muted-foreground">
                {activeTab === 'pending' ? 'Aucune photo en attente de validation' :
                 activeTab === 'approved' ? 'Aucune photo approuvée pour le moment' :
                 'Aucune photo refusée'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {currentPhotos.map(photo => (
              <Card key={photo.id} className="overflow-hidden" data-testid={`manage-photo-${photo.id}`}>
                <div className="aspect-[4/3] bg-gray-100 relative">
                  <img
                    src={photo.photo_base64}
                    alt={photo.caption || photo.menu_item_name}
                    className="w-full h-full object-cover"
                  />
                  <Badge className="absolute top-2 left-2 bg-black/60 text-white text-xs backdrop-blur-sm">
                    {photo.menu_item_name}
                  </Badge>
                </div>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{photo.user_pseudo}</p>
                      {photo.caption && <p className="text-xs text-muted-foreground">{photo.caption}</p>}
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {new Date(photo.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                    {activeTab === 'pending' && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="bg-emerald-500 hover:bg-emerald-600 text-white h-8 px-3 text-xs"
                          onClick={() => updateStatus(photo.id, 'approve')}
                          disabled={processing === photo.id}
                          data-testid={`approve-photo-${photo.id}`}
                        >
                          {processing === photo.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                          Approuver
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50 h-8 px-3 text-xs"
                          onClick={() => updateStatus(photo.id, 'reject')}
                          disabled={processing === photo.id}
                          data-testid={`reject-photo-${photo.id}`}
                        >
                          <X className="w-3.5 h-3.5 mr-1" />
                          Refuser
                        </Button>
                      </div>
                    )}
                    {activeTab === 'approved' && (
                      <Badge className="bg-emerald-100 text-emerald-700">Approuvée</Badge>
                    )}
                    {activeTab === 'rejected' && (
                      <Badge className="bg-red-100 text-red-700">Refusée</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
